/**
 * @boli/core - Core Package Entry Point
 *
 * This package contains all platform-agnostic business logic for Boli Live.
 * Used by BOTH Web (Phase 1) and Mobile (Phase 2) with ZERO changes.
 *
 * Architecture:
 * - Services: Business logic for bidding, payment, auth
 * - Utils: Time sync, idempotency, helpers
 * - Hooks: React hooks for UI integration
 */

// ============================================================================
// INTERNAL IMPORTS (for initializeBoliCore/destroyBoliCore)
// ============================================================================

import { createAuthService as _createAuthService } from './services/auth.service';
import { createBiddingService as _createBiddingService } from './services/bidding.service';
import { createPaymentService as _createPaymentService } from './services/payment.service';
import { createWalletService as _createWalletService } from './services/wallet.service';
import { createProductService as _createProductService } from './services/product.service';
import { createStoreService as _createStoreService } from './services/store.service';
import { createCartService as _createCartService } from './services/cart.service';
import { createStreamService as _createStreamService } from './services/stream.service';
import { createNotificationService as _createNotificationService } from './services/notification.service';
import { createEscrowService as _createEscrowService } from './services/escrow.service';
import { createKYCService as _createKYCService } from './services/kyc.service';
import { createMessageService as _createMessageService } from './services/message.service';
import { destroyTimeSyncService as _destroyTimeSyncService } from './utils/time-sync';
import { destroyIdempotencyService as _destroyIdempotencyService } from './utils/idempotency';

// ============================================================================
// SERVICES
// ============================================================================

export {
  BiddingService,
  createBiddingService,
  getBiddingService,
  type IBiddingService,
  type BiddingServiceConfig,
  type BidEligibility,
  DEFAULT_BIDDING_CONFIG,
} from './services/bidding.service';

export {
  PaymentService,
  createPaymentService,
  getPaymentService,
  type IPaymentService,
  type PaymentServiceConfig,
  type CardTokenizationRequest,
  type BiddingPassport,
  type PaymentBreakdown,
  DEFAULT_PAYMENT_CONFIG,
} from './services/payment.service';

export {
  AuthService,
  createAuthService,
  getAuthService,
  type IAuthService,
  type ISecureStorage,
  type AuthServiceConfig,
  type KYCDocuments,
  DEFAULT_AUTH_CONFIG,
} from './services/auth.service';

export {
  WalletService,
  createWalletService,
  getWalletService,
  type IWalletService,
  type WalletServiceConfig,
  DEFAULT_WALLET_CONFIG,
} from './services/wallet.service';

export {
  ProductService,
  createProductService,
  getProductService,
  type IProductService,
  type ProductServiceConfig,
  DEFAULT_PRODUCT_CONFIG,
} from './services/product.service';

export {
  StoreService,
  createStoreService,
  getStoreService,
  type IStoreService,
  type StoreServiceConfig,
  type StoreAuctionParams,
  type StoreAuctionResponse,
  DEFAULT_STORE_CONFIG,
} from './services/store.service';

export {
  CartService,
  createCartService,
  getCartService,
  type ICartService,
  type CartServiceConfig,
  DEFAULT_CART_CONFIG,
} from './services/cart.service';

export {
  StreamService,
  createStreamService,
  getStreamService,
  type IStreamService,
  type StreamServiceConfig,
  DEFAULT_STREAM_CONFIG,
} from './services/stream.service';

export {
  NotificationService,
  createNotificationService,
  getNotificationService,
  type INotificationService,
  type NotificationServiceConfig,
  DEFAULT_NOTIFICATION_CONFIG,
} from './services/notification.service';

export {
  EscrowService,
  createEscrowService,
  getEscrowService,
  type IEscrowService,
  type EscrowServiceConfig,
  DEFAULT_ESCROW_CONFIG,
} from './services/escrow.service';

export {
  KYCService,
  createKYCService,
  getKYCService,
  type IKYCService,
  type KYCServiceConfig,
  type KYCSubmitData,
  DEFAULT_KYC_CONFIG,
} from './services/kyc.service';

export {
  MessageService,
  createMessageService,
  getMessageService,
  type IMessageService,
  type MessageServiceConfig,
  DEFAULT_MESSAGE_CONFIG,
} from './services/message.service';

// ============================================================================
// UTILITIES
// ============================================================================

export {
  TimeSyncService,
  getTimeSyncService,
  destroyTimeSyncService,
  type TimeSyncConfig,
} from './utils/time-sync';

export {
  IdempotencyService,
  getIdempotencyService,
  destroyIdempotencyService,
  withIdempotency,
  type IdempotencyConfig,
} from './utils/idempotency';

// ============================================================================
// HOOKS
// ============================================================================

export {
  useAuction,
  useBids,
  usePlaceBid,
  useCountdown,
  useBidEligibility,
  useSuggestedBids,
  type UseAuctionOptions,
  type UseAuctionResult,
  type UseBidsResult,
  type UsePlaceBidResult,
  type UseCountdownResult,
  type UseBidEligibilityResult,
  type UseSuggestedBidsResult,
} from './hooks/useBidding';

// ============================================================================
// VERSION
// ============================================================================

export const CORE_VERSION = '1.0.0';

// ============================================================================
// INITIALIZATION HELPER
// ============================================================================

export interface BoliCoreConfig {
  apiBaseUrl: string;
  googleClientId: string;
  fetchAdapter: (url: string, options?: RequestInit) => Promise<Response>;
  secureStorage: {
    setItem: (key: string, value: string) => Promise<void>;
    getItem: (key: string) => Promise<string | null>;
    removeItem: (key: string) => Promise<void>;
    clear: () => Promise<void>;
  };
  realtimeAdapter?: {
    subscribe: (channel: string, callback: (data: unknown) => void) => () => void;
  };
  // Streaming options
  enableLiveVideo?: boolean;
}

/**
 * Initialize all Boli core services
 * Call this once at app startup
 */
export async function initializeBoliCore(config: BoliCoreConfig): Promise<void> {
  // Initialize auth service
  const authService = _createAuthService(
    {
      apiBaseUrl: config.apiBaseUrl,
      googleClientId: config.googleClientId,
    },
    config.secureStorage,
    config.fetchAdapter
  );
  await authService.initialize();

  // Initialize bidding service
  const biddingService = _createBiddingService(
    { apiBaseUrl: config.apiBaseUrl },
    config.fetchAdapter,
    config.realtimeAdapter
  );
  await biddingService.initialize(`${config.apiBaseUrl}/api/time`);

  // Initialize payment service
  _createPaymentService({ apiBaseUrl: config.apiBaseUrl }, config.fetchAdapter);

  // Initialize wallet service (Bidding Passport)
  _createWalletService(
    { apiBaseUrl: config.apiBaseUrl },
    config.fetchAdapter,
    () => authService.getToken()
  );

  // Initialize product service (Fixed-price listings)
  _createProductService(
    { apiBaseUrl: config.apiBaseUrl },
    config.fetchAdapter,
    () => authService.getToken()
  );

  // Initialize store service (Seller storefronts)
  _createStoreService(
    { apiBaseUrl: config.apiBaseUrl },
    config.fetchAdapter,
    () => authService.getToken()
  );

  // Initialize cart service (Shopping cart)
  _createCartService(
    { apiBaseUrl: config.apiBaseUrl },
    config.fetchAdapter,
    () => authService.getToken()
  );

  // Initialize stream service (Live video)
  _createStreamService(
    {
      apiBaseUrl: config.apiBaseUrl,
      enableLiveVideo: config.enableLiveVideo,
    },
    config.fetchAdapter,
    () => authService.getToken()
  );

  // Initialize notification service
  _createNotificationService(
    { apiBaseUrl: config.apiBaseUrl },
    config.fetchAdapter,
    () => authService.getToken()
  );

  // Initialize escrow service (buyer protection)
  _createEscrowService(
    { apiBaseUrl: config.apiBaseUrl },
    config.fetchAdapter,
    () => authService.getToken()
  );

  // Initialize KYC service (verification)
  _createKYCService(
    { apiBaseUrl: config.apiBaseUrl },
    config.fetchAdapter,
    () => authService.getToken()
  );

  // Initialize message service (buyer-seller chat)
  _createMessageService(
    { apiBaseUrl: config.apiBaseUrl },
    config.fetchAdapter,
    () => authService.getToken()
  );

  console.log('Boli Core initialized successfully');
}

/**
 * Cleanup all Boli core services
 * Call this when app is closing
 */
export function destroyBoliCore(): void {
  _destroyTimeSyncService();
  _destroyIdempotencyService();
  console.log('Boli Core destroyed');
}
